/**
 * @file api.ts
 * @brief React Query hooks for Personal Notes on the web panel.
 *
 * @details
 * Every route here is USER-scoped server-side (`owner_id = req.user.id`), so
 * these hooks return the caller's own notes whatever their role — a company
 * admin reads their notes, never a member's. Nothing in this file takes a
 * company id, and nothing should ever be given one.
 *
 * **The web writes TYPED notes only.** `POST /notes` with a `text` body is what
 * makes a note typed; typed notes are born `ready`, are unmetered, and never
 * run ASR or insights. There is deliberately no create-from-audio hook, no
 * upload hook and no `regenerate-insights` hook here: recording stays on
 * mobile, and adding an insights call would put a paid, AI-backed path behind a
 * surface built for the free one.
 *
 * @dependencies
 * - @tanstack/react-query
 * - shared/api/client (apiGet/apiPost/apiPatch/apiDelete → ApiClientError)
 * - shared/api/endpoints: notesEndpoints, boardroomEndpoints
 *
 * @references
 * - BACKEND/src/features/notes/routes.js
 * - BACKEND/src/features/boardroom/routes.js (prep-notes attach/detach)
 * - FRONTEND/src/screens/Notes/notesStorage.ts (the mobile twin)
 * - docs/decisions/ADR-023-note-meeting-links.md
 *
 * @author DES
 * @date 21-08-2026
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiClientError, apiDelete, apiGet, apiPatch, apiPost } from "@/shared/api/client";
import { boardroomEndpoints, notesEndpoints } from "@/shared/api/endpoints";
// Attaching changes what the MEETING shows, so the meeting's attached-notes
// cache has to be invalidated from here. One-directional: company-meetings
// never imports this module. (21-08-2026)
import { companyMeetingsKeys } from "@/features/company-meetings/api";
import {
  NoteLinksResponseSchema,
  NoteMeetingReportResponseSchema,
  NoteMeetingReportsResponseSchema,
  NoteResponseSchema,
  NoteTagsResponseSchema,
  NotesListResponseSchema,
  type NoteLink,
  type NoteMeetingReport,
  type NoteMeetingReportEntry,
  type NoteKind,
  type NotesListParams,
  type VoiceNote,
} from "./schemas";

// ----------------------------------------------------------------------
// Query keys
// ----------------------------------------------------------------------
export const notesKeys = {
  all: ["notes"] as const,
  /** Prefix for EVERY page/search combination — invalidating one params object
   *  would leave every other page stale, which is how a new note fails to
   *  appear on the list the user is actually looking at. */
  lists: ["notes", "list"] as const,
  list: (params: NotesListParams) => ["notes", "list", params] as const,
  detail: (id: string) => ["notes", "detail", id] as const,
  tags: ["notes", "tags"] as const,
  links: (id: string) => ["notes", "links", id] as const,
  /** "My notes x this meeting" reports for one note (06-10-2026). */
  meetingReports: (id: string) => ["notes", "meeting-reports", id] as const,
};

// ----------------------------------------------------------------------
// Reads
// ----------------------------------------------------------------------

/**
 * Paginated list. Search is SERVER-side and covers more than the visible text —
 * title, transcript, tags, insights and the created date, including full month
 * names. Filtering the loaded page in the browser would silently miss every
 * note on a page that has not been fetched.
 */
export function useNotesList(params: NotesListParams, enabled = true) {
  return useQuery({
    queryKey: notesKeys.list(params),
    enabled,
    queryFn: async () => {
      const search = new URLSearchParams();
      if (params.page) search.set("page", String(params.page));
      if (params.pageSize) search.set("pageSize", String(params.pageSize));
      if (params.search?.trim()) search.set("search", params.search.trim());
      if (params.kind) search.set("kind", params.kind);
      const qs = search.toString();
      const raw = await apiGet<unknown>(qs ? `${notesEndpoints.list}?${qs}` : notesEndpoints.list);
      const parsed = NotesListResponseSchema.parse(raw);
      return {
        items: parsed.notes ?? [],
        page: parsed.page ?? params.page ?? 1,
        total: parsed.total ?? 0,
        hasMore: parsed.hasMore ?? false,
      };
    },
    placeholderData: (prev) => prev,
  });
}

/** A note someone else shared with you: read-only, with who shared it. */
export type SharedNote = VoiceNote & {
  shared_by_name?: string | null;
  shared_at?: string | null;
};

/**
 * Notes other people shared with the viewer — GET /notes/shared-with-me
 * (ADR-027). Newest share first; empty when sharing is switched off. The
 * library's right-hand column (DES 30-09-2026). Up to 100, the API's cap.
 */
export function useSharedWithMeNotes(enabled = true) {
  return useQuery<SharedNote[]>({
    queryKey: ["notes", "shared-with-me"],
    enabled,
    staleTime: 60_000,
    queryFn: async () =>
      (await apiGet<{ notes?: SharedNote[] }>("/api/notes/shared-with-me?limit=100")).notes ?? [],
  });
}

/**
 * The library as one growing list, the way the phone reads it: 20 at a time,
 * the next page appended as the reader nears the end (30-09-2026). Lives under
 * `notesKeys.lists`, so every write that refreshes the paged list refreshes
 * this one too.
 */
export function useNotesInfinite(
  search?: string,
  pageSize = 20,
  /** ISO bounds on when the note was made (01-10-2026). */
  range?: { from?: string | null; to?: string | null },
  /** Notes or agendas only (DES 12-10-2026). Omitted = every note. */
  kind?: NoteKind,
) {
  const term = search?.trim() || "";
  const from = range?.from ?? "";
  const to = range?.to ?? "";
  const k = kind ?? "";
  return useInfiniteQuery({
    queryKey: [...notesKeys.lists, "infinite", term, pageSize, from, to, k] as const,
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const qs = new URLSearchParams({
        page: String(pageParam),
        pageSize: String(pageSize),
      });
      if (term) qs.set("search", term);
      if (from) qs.set("from", from);
      if (to) qs.set("to", to);
      if (k) qs.set("kind", k);
      const raw = await apiGet<unknown>(`${notesEndpoints.list}?${qs}`);
      const parsed = NotesListResponseSchema.parse(raw);
      return {
        items: parsed.notes ?? [],
        page: parsed.page ?? pageParam,
        total: parsed.total ?? 0,
        hasMore: parsed.hasMore ?? false,
      };
    },
    getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    placeholderData: (prev) => prev,
  });
}

/**
 * One note.
 *
 * Polls only while a VOICE note is still being transcribed or analysed. A typed
 * note is born `ready` and nothing runs behind it, so polling one would be a
 * request every few seconds that can never return anything new.
 */
export function useNote(id: string | null, enabled = true) {
  return useQuery<VoiceNote>({
    queryKey: notesKeys.detail(id ?? ""),
    enabled: enabled && Boolean(id),
    queryFn: async () => {
      const raw = await apiGet<unknown>(notesEndpoints.byId(id!));
      return NoteResponseSchema.parse(raw).note;
    },
    refetchInterval: (query) => {
      const note = query.state.data;
      if (!note || note.source === "typed") {
        return false;
      }
      return note.status === "processing" || note.status === "analyzing" ? 4000 : false;
    },
  });
}

/** Distinct tags across the caller's notes, most-used first. */
export function useNoteTags(enabled = true) {
  return useQuery({
    queryKey: notesKeys.tags,
    enabled,
    queryFn: async () => {
      const raw = await apiGet<unknown>(notesEndpoints.tags);
      return NoteTagsResponseSchema.parse(raw).tags ?? [];
    },
    staleTime: 5 * 60_000,
  });
}

/** Meetings this note is attached to. */
export function useNoteLinks(id: string | null, enabled = true) {
  return useQuery<NoteLink[]>({
    queryKey: notesKeys.links(id ?? ""),
    enabled: enabled && Boolean(id),
    queryFn: async () => {
      const raw = await apiGet<unknown>(notesEndpoints.links(id!));
      return NoteLinksResponseSchema.parse(raw).links ?? [];
    },
  });
}

// ----------------------------------------------------------------------
// "My notes x this meeting" (web port of the phone's NoteMeetingReports,
// 06-10-2026)
// null = the server does not offer reports (404 switched off, 503 not set up):
// the section hides, as on the phone. Building one is a mutation, never a
// query, because it is a paid model call that must not fire on a refetch.
// Related: NoteMeetingReports.tsx, BACKEND features/notes/routes.js
// ----------------------------------------------------------------------
export type NoteMeetingReportsData = {
  pointCount: number;
  meetings: NoteMeetingReportEntry[];
};

export function useNoteMeetingReports(id: string | null, enabled = true) {
  return useQuery<NoteMeetingReportsData | null>({
    queryKey: notesKeys.meetingReports(id ?? ""),
    enabled: enabled && Boolean(id),
    queryFn: async () => {
      try {
        const raw = await apiGet<unknown>(notesEndpoints.meetingReports(id!));
        const parsed = NoteMeetingReportsResponseSchema.parse(raw);
        return {
          pointCount: Number(parsed.note?.pointCount ?? 0),
          meetings: parsed.meetings ?? [],
        };
      } catch (err) {
        if (err instanceof ApiClientError && (err.status === 404 || err.status === 503)) {
          return null;
        }
        throw err;
      }
    },
    staleTime: 30_000,
  });
}

export type GenerateNoteMeetingReportVars = {
  noteId: string;
  meetingId: string;
  /** Rebuild even when nothing changed. */
  force?: boolean;
};

export function useGenerateNoteMeetingReport() {
  const qc = useQueryClient();
  return useMutation<NoteMeetingReport, unknown, GenerateNoteMeetingReportVars>({
    mutationFn: async ({ noteId, meetingId, force }) => {
      const raw = await apiPost<unknown>(
        notesEndpoints.meetingReport(noteId, meetingId),
        { force: force === true },
        // The server waits on the model (10-30s); the client default is 30s.
        { timeout: 120_000 },
      );
      return NoteMeetingReportResponseSchema.parse(raw).report;
    },
    onSettled: (_res, _err, vars) => {
      void qc.invalidateQueries({ queryKey: notesKeys.meetingReports(vars.noteId) });
    },
  });
}

// ----------------------------------------------------------------------
// Writes
// ----------------------------------------------------------------------

export type CreateTypedNoteVars = {
  title?: string;
  /** The points, newline-joined. Blank lines are dropped before sending. */
  text: string;
  /**
   * Recorder notepad (ADR-030, 06-10-2026): the second of the recording each
   * non-blank line of `text` was typed at, in order. The server drops the
   * array when its length does not match the lines.
   */
  pointOffsets?: Array<number | null>;
  /** Sub-points (DES 12-10-2026): 0 / 1 per non-blank line of `text`. */
  pointLevels?: number[];
  /** 'agenda' when written from the Agendas tab or a meeting's agenda
   *  picker (DES 12-10-2026). Omitted = a note. */
  kind?: NoteKind;
};

/**
 * Create a typed note.
 *
 * The id is minted server-side: only the recorded path needs a client id (the
 * GCS prefix is built from it), and a typed note never touches storage.
 */
export function useCreateTypedNote() {
  const qc = useQueryClient();
  return useMutation<VoiceNote, unknown, CreateTypedNoteVars>({
    mutationFn: async (vars) => {
      const raw = await apiPost<unknown>(notesEndpoints.create, {
        title: vars.title?.trim() || undefined,
        text: vars.text,
        ...(vars.pointOffsets ? { pointOffsets: vars.pointOffsets } : {}),
        ...(vars.pointLevels ? { pointLevels: vars.pointLevels } : {}),
        ...(vars.kind === "agenda" ? { kind: "agenda" } : {}),
      });
      return NoteResponseSchema.parse(raw).note;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notesKeys.all });
    },
  });
}

export type UpdateNoteVars = {
  id: string;
  title?: string;
  isPinned?: boolean;
  tags?: string[];
  /** Typed notes only — the server refuses `text` on a voice note. */
  text?: string;
  /**
   * The points, on EITHER kind of note (ADR-025). The transcript stays
   * read-only — it is the record of what was said — while the points are the
   * working copy, so a wrong one can be fixed. Saving stamps `points_edited_at`
   * server-side, after which regeneration refuses rather than overwriting.
   */
  points?: string[];
  /**
   * Recorder notepad (ADR-030, 06-10-2026): aligned with `points`. The server
   * stores it only alongside `points` and clears it on any points edit that
   * comes without it, so a stale map never points a line at the wrong second.
   */
  pointOffsets?: Array<number | null>;
  /** Sub-points (DES 12-10-2026): 0 / 1 aligned with `points`. An all-zero
   *  array is fine; the server stores null. */
  pointLevels?: number[];
  /** Move a note to Agendas, or an agenda back to Notes (DES 12-10-2026). */
  kind?: NoteKind;
};

export function useUpdateNote() {
  const qc = useQueryClient();
  return useMutation<VoiceNote, unknown, UpdateNoteVars>({
    mutationFn: async ({ id, ...body }) => {
      const raw = await apiPatch<unknown>(notesEndpoints.update(id), body);
      return NoteResponseSchema.parse(raw).note;
    },
    onSuccess: (note) => {
      qc.setQueryData(notesKeys.detail(note.id), note);
      void qc.invalidateQueries({ queryKey: notesKeys.lists });
      // A tag that did not exist before is now a suggestion for every note.
      void qc.invalidateQueries({ queryKey: notesKeys.tags });
    },
  });
}

export type RegeneratePointsVars = {
  noteId: string;
  /** Required once the points have been edited — the server answers 409 and
   *  the caller has to ask again on purpose. */
  force?: boolean;
};

/**
 * Re-derive a recording's summary and points.
 *
 * The action-item toggle that used to live here is gone with its endpoint
 * (ADR-025): a tick promised follow-through this product does not provide.
 */
export function useRegenerateNotePoints() {
  const qc = useQueryClient();
  return useMutation<void, unknown, RegeneratePointsVars>({
    mutationFn: async ({ noteId, force }) => {
      await apiPost(notesEndpoints.regenerate(noteId), { force: force === true });
    },
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: notesKeys.detail(vars.noteId) });
    },
  });
}

export function useDeleteNote() {
  const qc = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: async (id) => {
      await apiDelete(notesEndpoints.delete(id));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notesKeys.all });
    },
  });
}

// ----------------------------------------------------------------------
// Attach / detach (ADR-023)
//
// The ROLE is the server's decision, not the caller's — it depends on whether
// the meeting has been processed — so it is echoed back and the UI reports what
// actually happened rather than what the row predicted. A 409 (`MEETING_BUSY`)
// arrives as an ApiClientError carrying the server's own message, which is
// actionable ("try again once it is ready") and must be shown, not swallowed.
// ----------------------------------------------------------------------
export type AttachNoteVars = {
  meetingId: string;
  noteId: string;
  /** Visible to the meeting's other participants. Defaults to PRIVATE — the
   *  one mistake that cannot be taken back is publishing by accident. */
  shared?: boolean;
};

export function useAttachNoteToMeeting() {
  const qc = useQueryClient();
  return useMutation<{ role: "prep" | "addendum"; shared: boolean }, unknown, AttachNoteVars>({
    mutationFn: async ({ meetingId, noteId, shared }) => {
      const raw = (await apiPost(boardroomEndpoints.prepNotes(meetingId), {
        noteId,
        shared: shared === true,
      })) as { role?: "prep" | "addendum"; shared?: boolean };
      return {
        role: raw?.role === "addendum" ? "addendum" : "prep",
        shared: raw?.shared === true,
      };
    },
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: notesKeys.links(vars.noteId) });
      void qc.invalidateQueries({ queryKey: notesKeys.lists });
      void qc.invalidateQueries({
        queryKey: companyMeetingsKeys.attachedNotes(vars.meetingId),
      });
    },
  });
}

/**
 * Change one attachment's visibility without detaching it.
 *
 * Author-only server-side: the meeting's owner may remove a contribution but
 * cannot publish someone else's writing to the room, nor withdraw it for them.
 */
export function useSetNoteMeetingShared() {
  const qc = useQueryClient();
  return useMutation<void, unknown, Required<AttachNoteVars>>({
    mutationFn: async ({ meetingId, noteId, shared }) => {
      await apiPatch(`${boardroomEndpoints.prepNotes(meetingId)}/${encodeURIComponent(noteId)}`, {
        shared,
      });
    },
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({
        queryKey: companyMeetingsKeys.attachedNotes(vars.meetingId),
      });
      void qc.invalidateQueries({ queryKey: notesKeys.links(vars.noteId) });
    },
  });
}

export function useDetachNoteFromMeeting() {
  const qc = useQueryClient();
  return useMutation<void, unknown, AttachNoteVars>({
    mutationFn: async ({ meetingId, noteId }) => {
      await apiDelete(`${boardroomEndpoints.prepNotes(meetingId)}/${encodeURIComponent(noteId)}`);
    },
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: notesKeys.links(vars.noteId) });
      void qc.invalidateQueries({ queryKey: notesKeys.lists });
      void qc.invalidateQueries({
        queryKey: companyMeetingsKeys.attachedNotes(vars.meetingId),
      });
    },
  });
}
